// Spikes Effect (Underground Black Spikes Eruption and Retaliation)
// Features:
// 1. RUMBLE: Screen shakes, thick black steam/smoke vents randomly across the gaming board.
// 2. ERUPT: A massive cluster of black spikes violently erupts randomly across the board.
// 3. PUNISH: A second, more violent wave of spikes erupts across the board.
// 4. DISSIPATE: The spikes crumble into dark ash and the smoke slowly clears.

import { Particle } from '../ParticleLayer';

const CONFIG = {
    spikeScale: 0.6, // Base scale for the SVG
    rumbleDur: 400,
    eruptDur: 300,
    punishDur: 300,
    dissipateDur: 600,
    paths: {
        spike: '/assets/effects/spikes/spikes.svg',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.spike);

class BlackSteam {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    maxLife: number; rotation: number; rotSpeed: number;

    constructor(x: number, y: number, power: number = 1.0) {
        this.x = x;
        this.y = y;

        // Venting upwards and slightly outwards
        const speed = (1 + Math.random() * 3) * power;
        const emitAngle = -Math.PI / 2 + (Math.random() - 0.5) * 1.5;
        this.vx = Math.cos(emitAngle) * speed;
        this.vy = Math.sin(emitAngle) * speed;

        this.maxLife = 800 + Math.random() * 600;
        this.life = this.maxLife;
        this.size = (40 + Math.random() * 60) * power; // Larger smoke for global scale
        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.05;
    }

    update(dt: number) {
        this.x += this.vx; this.y += this.vy;
        this.vx *= 0.95; this.vy *= 0.95; this.vy -= 0.3; // drift up faster
        this.life -= dt; this.size += dt * 0.08; this.rotation += this.rotSpeed;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const alpha = Math.sin((this.life / this.maxLife) * Math.PI) * 0.6; // Darker/thicker smoke
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(this.x, this.y); ctx.rotate(this.rotation);

        ctx.beginPath(); ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size);
        // Deep obsidian smoke
        grad.addColorStop(0, 'rgba(9, 9, 11, 0.9)');
        grad.addColorStop(0.5, 'rgba(24, 24, 27, 0.7)');
        grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = grad; ctx.fill();
        ctx.restore();
    }
}

class SpikeInstance {
    x: number;
    yBase: number; // The ground level for this spike
    targetYOffset: number; // How far up from ground it thrusts
    scale: number;
    rotation: number;
    delay: number;

    constructor(canvasWidth: number, canvasHeight: number) {
        // Spread entirely across the screen width (with some padding)
        this.x = (canvasWidth * 0.1) + Math.random() * (canvasWidth * 0.8);

        // Spread vertically across the middle "board" section of the screen
        // Roughly from 30% to 80% of screen height
        this.yBase = canvasHeight * 0.3 + Math.random() * (canvasHeight * 0.5);

        // Random tilt relative to the center of the screen
        const screenCenterX = canvasWidth / 2;
        const biasToCenter = (this.x - screenCenterX) / screenCenterX; // -1 to 1
        const angleFan = biasToCenter * (Math.PI / 6); // Points outwards from center
        this.rotation = angleFan + (Math.random() - 0.5) * 0.2;

        this.scale = CONFIG.spikeScale * (0.5 + Math.random() * 0.8);

        // End up piercing above exactly where it started
        this.targetYOffset = -40 - Math.random() * 80;

        this.delay = Math.random() * 200; // Staggered eruption
    }
}

export type SpikesEffect = {
    kind: 'spikes'
    t: number
    dx: number; dy: number; // Not used as center anymore, but kept for signature compatibility
    atx: number; aty: number;
    data?: {
        phase: 'RUMBLE' | 'ERUPT' | 'PUNISH' | 'DISSIPATE'
        steam: BlackSteam[]
        defSpikes: SpikeInstance[]
        attSpikes: SpikeInstance[]
        sparkedPunish: boolean
    }
}

export const createSpikesEffect = (dx: number, dy: number, atx: number, aty: number): SpikesEffect => ({
    kind: 'spikes', t: 0, dx, dy, atx, aty
});

export const renderSpikesEffect = (
    eff: SpikesEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    if (!eff.data) {
        const cw = ctx.canvas.width;
        const ch = ctx.canvas.height;

        // Initialize spikes universally across the board
        const defSpikes: SpikeInstance[] = [];
        for (let i = 0; i < 12; i++) defSpikes.push(new SpikeInstance(cw, ch));

        const attSpikes: SpikeInstance[] = [];
        // The punish spikes are even more numerous
        for (let i = 0; i < 18; i++) attSpikes.push(new SpikeInstance(cw, ch));

        eff.data = {
            phase: 'RUMBLE',
            steam: [],
            defSpikes,
            attSpikes,
            sparkedPunish: false
        };
    }

    const d = eff.data;
    const dt = 16.7; // ~60fps

    const tRum = CONFIG.rumbleDur;
    const tErupt = tRum + CONFIG.eruptDur;
    const tPunish = tErupt + CONFIG.punishDur;
    const tTotal = tPunish + CONFIG.dissipateDur;

    if (eff.t < tRum) d.phase = 'RUMBLE';
    else if (eff.t < tErupt) d.phase = 'ERUPT';
    else if (eff.t < tPunish) d.phase = 'PUNISH';
    else d.phase = 'DISSIPATE';

    ctx.save();

    let globalAlpha = 1;
    const cw = ctx.canvas.width;
    const ch = ctx.canvas.height;

    // Helper to spawn global steam
    const spawnGlobalSteam = (power: number) => {
        const randX = (cw * 0.1) + Math.random() * (cw * 0.8);
        const randY = ch * 0.3 + Math.random() * (ch * 0.5);
        d.steam.push(new BlackSteam(randX, randY, power));
    };

    if (d.phase === 'RUMBLE') {
        const p = eff.t / tRum;

        // Venting steam continuously everywhere
        if (Math.random() < 0.6) spawnGlobalSteam(0.8);
        if (Math.random() < 0.4) spawnGlobalSteam(1.0);

        // Pre-eruption global shake
        ctx.translate((Math.random() - 0.5) * 6 * p, (Math.random() - 0.5) * 6 * p);

    } else if (d.phase === 'ERUPT') {
        const p = (eff.t - tRum) / CONFIG.eruptDur;

        if (Math.random() < 0.8) spawnGlobalSteam(1.2);

        // Heavy rumble as spikes break surface
        ctx.translate((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 10);

    } else if (d.phase === 'PUNISH') {
        const p = (eff.t - tErupt) / CONFIG.punishDur;

        if (Math.random() < 0.8) spawnGlobalSteam(1.5);

        // Violent shake on the retaliation impact
        const shake = 20 * (1 - p);
        ctx.translate((Math.random() - 0.5) * shake, (Math.random() - 0.5) * shake);

        if (!d.sparkedPunish && p > 0.1) {
            d.sparkedPunish = true;
            // Massive blood and rust global explosion
            for (let i = 0; i < 80; i++) {
                spawnParticle({
                    x: cw * 0.1 + Math.random() * cw * 0.8,
                    y: ch * 0.2 + Math.random() * ch * 0.6,
                    vx: (Math.random() - 0.5) * 25,
                    vy: -10 - Math.random() * 30,
                    life: 400 + Math.random() * 600,
                    color: i % 4 === 0 ? '#dc2626' : (i % 2 === 0 ? '#3f3f46' : '#18181b'), // Blood and dark rust
                    size: 3 + Math.random() * 5
                });
            }
        }

    } else if (d.phase === 'DISSIPATE') {
        const p = (eff.t - tPunish) / CONFIG.dissipateDur;
        globalAlpha = 1 - p;

        // Spikes crumble into ash everywhere
        if (Math.random() < 0.8 && globalAlpha > 0.2) {
            for (let i = 0; i < 3; i++) {
                spawnParticle({
                    x: cw * 0.1 + Math.random() * cw * 0.8,
                    y: ch * 0.2 + Math.random() * ch * 0.6,
                    vx: (Math.random() - 0.5) * 5,
                    vy: 1 + Math.random() * 4, // flakes falling
                    life: 300 + Math.random() * 400,
                    color: '#27272a',
                    size: 2 + Math.random() * 3
                });
            }
        }
    }

    // Draw steam first so spikes erupt through it
    d.steam = d.steam.filter(s => s.life > 0);
    d.steam.forEach(s => { s.update(dt); s.draw(ctx); });

    const img = getSprite(CONFIG.paths.spike);
    if (!img.complete || img.naturalWidth === 0) {
        ctx.restore();
        return eff.t < tTotal;
    }

    // Sort all visible spikes by Y to render them correctly in pseudo-3D
    let visibleSpikes: { inst: SpikeInstance, p: number, localT: number, duration: number }[] = [];

    // Helper to calculate state
    const processSpike = (inst: SpikeInstance, localT: number, duration: number) => {
        if (localT < inst.delay) return;

        let p = (localT - inst.delay) / (duration * 0.5);
        if (p > 1) p = 1;

        visibleSpikes.push({ inst, p, localT, duration });
    };

    // Calculate states
    let defLocalT = Math.max(0, eff.t - tRum);
    d.defSpikes.forEach(sp => processSpike(sp, defLocalT, CONFIG.eruptDur));

    let attLocalT = Math.max(0, eff.t - tErupt);
    d.attSpikes.forEach(sp => processSpike(sp, attLocalT, CONFIG.punishDur));

    // Sort by Y-base so closer spikes render on top
    visibleSpikes.sort((a, b) => a.inst.yBase - b.inst.yBase);

    // Render sorted spikes
    visibleSpikes.forEach(({ inst, p }) => {
        const easeOutBack = (x: number): number => {
            const c1 = 1.70158;
            const c3 = c1 + 1;
            return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
        };

        let currentY = inst.yBase + 200; // start 200px underground
        if (p > 0) {
            currentY = inst.yBase + 200 + ((inst.yBase + inst.targetYOffset) - (inst.yBase + 200)) * easeOutBack(p);
        }

        ctx.save();
        ctx.globalAlpha = globalAlpha;

        // Dissipate crumble
        if (d.phase === 'DISSIPATE') {
            const dp = (eff.t - tPunish) / CONFIG.dissipateDur;
            currentY += dp * 30; // sink deeply
            ctx.translate((Math.random() - 0.5) * 4 * (1 - dp), (Math.random() - 0.5) * 4 * (1 - dp));
        }

        ctx.translate(inst.x, currentY);
        ctx.rotate(inst.rotation);
        ctx.scale(inst.scale, inst.scale);

        if (p > 0 && p < 1) {
            ctx.scale(1, 1 + (1 - p) * 0.4);
        }

        const aspect = img.naturalWidth / img.naturalHeight;
        const renderHeight = 400;
        const renderWidth = renderHeight * aspect;

        ctx.drawImage(img, -renderWidth / 2, -renderHeight, renderWidth, renderHeight);
        ctx.restore();
    });

    ctx.restore();
    return eff.t < tTotal;
};
